/*
 * oum-visionneuse.js : visionneuse plein écran des images produit (balisage : snippets/oum-visionneuse ; styles :
 * assets/oum-visionneuse.css), reprise de celle d'Apyforme.
 * - <dialog> ouvert en showModal() : couche supérieure du navigateur (au-dessus de l'en-tête et des bulles), reste de
 *   la page inerte, Échap natif. Défilement de la page bloqué pendant l'ouverture.
 * - Piste en scroll-snap : glissé natif au doigt et au trackpad ; la diapositive courante se déduit du défilement.
 * - Flèches, clavier (← → Début Fin), vignettes, compteur annoncé aux lecteurs d'écran.
 * - Zoom (souris uniquement) : clic = ×2 au point visé, l'image suit la souris ; nouveau clic, Échap ou changement
 *   de diapositive = retour. Sur mobile, le pincement natif reste disponible.
 * Ouverture : clic sur [data-oum-visionneuse="ID"][data-oum-visionneuse-index] (délégué), ou element.ouvrir(index).
 */
(() => {
  'use strict';

  const ZOOM = 2;
  const souris = matchMedia('(hover: hover) and (pointer: fine)');
  const sansAnimation = matchMedia('(prefers-reduced-motion: reduce)');

  // L'image remplit sa diapositive en object-fit: contain : rectangle réellement occupé par l'image
  const cadreImage = (image) => {
    const r = image.getBoundingClientRect();
    const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : r.width / r.height;
    const l = Math.min(r.width, r.height * ratio);
    const h = l / ratio;
    return { left: r.left + (r.width - l) / 2, top: r.top + (r.height - h) / 2, width: l, height: h };
  };
  const surImage = (image, e) => {
    const c = cadreImage(image);
    return e.clientX >= c.left && e.clientX <= c.left + c.width && e.clientY >= c.top && e.clientY <= c.top + c.height;
  };

  class OumVisionneuse extends HTMLElement {
    connectedCallback() {
      if (this._pret) return;
      this._pret = true;
      this.dialogue = this.querySelector('dialog');
      this.piste = this.querySelector('[data-oum-v-piste]');
      this.diapos = [...this.querySelectorAll('[data-oum-v-diapo]')];
      this.vignettes = [...this.querySelectorAll('[data-oum-v-vignette]')];
      this.bandeau = this.querySelector('[data-oum-v-vignettes]');
      this.compteur = this.querySelector('[data-oum-v-actuel]');
      this.prec = this.querySelector('[data-oum-v-prec]');
      this.suiv = this.querySelector('[data-oum-v-suiv]');
      this.actuel = 0;

      this.querySelector('[data-oum-v-fermer]').addEventListener('click', () => this.fermer());
      if (this.prec) this.prec.addEventListener('click', () => this.aller(this.actuel - 1));
      if (this.suiv) this.suiv.addEventListener('click', () => this.aller(this.actuel + 1));
      this.vignettes.forEach((v) => v.addEventListener('click', () => this.aller(Number(v.dataset.oumVVignette))));

      let image = 0;
      this.piste.addEventListener('scroll', () => {
        if (!image) image = requestAnimationFrame(() => { image = 0; this._suivreDefilement(); });
      }, { passive: true });

      // Clic sur le blanc autour de l'image : fermeture ; sur l'image elle-même (souris) : zoom
      this.piste.addEventListener('click', (e) => {
        const diapo = e.target.closest('[data-oum-v-diapo]');
        if (!diapo) return;
        const img = diapo.querySelector('img');
        if (this._zoom) { this._sansZoom(); return; }
        if (img && surImage(img, e)) { if (souris.matches) this._basculerZoom(img, e); return; }
        this.fermer();
      });
      this.piste.addEventListener('mousemove', (e) => {
        if (this._zoom) { this._suivreSouris(e); return; }
        const diapo = e.target.closest('[data-oum-v-diapo]');
        const img = diapo && diapo.querySelector('img');
        if (img) diapo.classList.toggle('is-sur-image', surImage(img, e));
      });

      // Clavier écouté sur tout le document pendant l'ouverture : un clic dans l'image ne doit pas le couper
      this._clavierDocument = (e) => this._clavier(e);
      // Échap : d'abord sortir du zoom
      this.dialogue.addEventListener('cancel', (e) => { if (this._zoom) { e.preventDefault(); this._sansZoom(); } });
      this.dialogue.addEventListener('close', () => this._apresFermeture());
      this._redimensionner = () => {
        this._sansZoom();
        this.piste.scrollTo({ left: this.actuel * this.piste.clientWidth, behavior: 'instant' });
      };
    }

    disconnectedCallback() {
      if (this.dialogue && this.dialogue.open) this.dialogue.close();
    }

    ouvrir(index = 0, declencheur = null) {
      this._declencheur = declencheur || document.activeElement;
      if (!this.dialogue.open) {
        // Page figée sans saut : la largeur de la barre de défilement disparue est compensée
        const barre = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--oum-barre-defilement', `${barre}px`);
        document.documentElement.classList.add('oum-visionneuse-ouverte');
        this.dialogue.showModal();
        window.addEventListener('resize', this._redimensionner);
        document.addEventListener('keydown', this._clavierDocument);
      }
      this.aller(index, false);
    }

    fermer() {
      if (this.dialogue.open) this.dialogue.close();
    }

    aller(index, anime = true) {
      const i = Math.max(0, Math.min(this.diapos.length - 1, index));
      this._sansZoom();
      this.piste.scrollTo({ left: i * this.piste.clientWidth, behavior: anime && !sansAnimation.matches ? 'smooth' : 'instant' });
      this._fixer(i);
    }

    _suivreDefilement() {
      const i = Math.round(this.piste.scrollLeft / Math.max(1, this.piste.clientWidth));
      if (i !== this.actuel) { this._sansZoom(); this._fixer(i); }
    }

    _fixer(i) {
      this.actuel = i;
      if (this.compteur) this.compteur.textContent = String(i + 1);
      if (this.prec) this.prec.disabled = i === 0;
      if (this.suiv) this.suiv.disabled = i === this.diapos.length - 1;
      this.vignettes.forEach((v, n) => v.setAttribute('aria-current', n === i ? 'true' : 'false'));
      const v = this.vignettes[i];
      if (v && this.bandeau) {
        this.bandeau.scrollTo({ left: v.offsetLeft - (this.bandeau.clientWidth - v.offsetWidth) / 2, behavior: sansAnimation.matches ? 'instant' : 'smooth' });
      }
    }

    _clavier(e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      const actions = { ArrowLeft: this.actuel - 1, ArrowRight: this.actuel + 1, Home: 0, End: this.diapos.length - 1 };
      if (!(e.key in actions)) return;
      e.preventDefault();
      this.aller(actions[e.key]);
    }

    _basculerZoom(image, e) {
      const deja = this._zoom === image;
      this._sansZoom();
      if (deja) return;
      this._zoom = image;
      this._cadre = image.getBoundingClientRect(); // boîte avant agrandissement (l'origine du zoom s'y exprime)
      this._suivreSouris(e);
      image.closest('[data-oum-v-diapo]').classList.add('is-zoom');
      image.style.transform = `scale(${ZOOM})`;
    }

    _suivreSouris(e) {
      const r = this._cadre;
      const x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
      const y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
      this._zoom.style.transformOrigin = `${x}% ${y}%`;
    }

    _sansZoom() {
      if (!this._zoom) return;
      this._zoom.style.transform = '';
      this._zoom.closest('[data-oum-v-diapo]').classList.remove('is-zoom');
      this._zoom = null;
    }

    _apresFermeture() {
      this._sansZoom();
      window.removeEventListener('resize', this._redimensionner);
      document.removeEventListener('keydown', this._clavierDocument);
      document.documentElement.classList.remove('oum-visionneuse-ouverte');
      document.documentElement.style.removeProperty('--oum-barre-defilement');
      const d = this._declencheur;
      this._declencheur = null;
      if (d && d.isConnected && typeof d.focus === 'function') d.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('oum-visionneuse')) customElements.define('oum-visionneuse', OumVisionneuse);

  // Ouverture déléguée : fonctionne aussi après un rechargement de la section dans l'éditeur
  document.addEventListener('click', (e) => {
    const declencheur = e.target instanceof Element && e.target.closest('[data-oum-visionneuse]');
    if (!declencheur) return;
    const visionneuse = document.getElementById(declencheur.dataset.oumVisionneuse);
    if (!visionneuse || typeof visionneuse.ouvrir !== 'function') return;
    e.preventDefault();
    visionneuse.ouvrir(Number(declencheur.dataset.oumVisionneuseIndex) || 0, declencheur);
  });
})();
